import React, { useState, useEffect } from "react";
import CourseList from "../../component/CourseList";
import QuotaList from "../../component/QuotaList";
import axios from "axios";
import CreateLessonModal from "./components/CreateLessonModal";
import {
  PlusCircle,
  BookOpen,
  Video,
  FileText,
  Loader2,
  Search,
  Filter,
  ChevronDown,
  Edit,
  MoreHorizontal,
  Eye,
  Trash2,
} from "lucide-react";
import LessonUpdateModal from "./components/UpdateLessonModal";
import LessonSkeletons from "./components/LessonManagerSkeleton";
const apiBase = process.env.REACT_APP_API;

const LessonManager = () => {
  // Get initial values from URL params
  const searchParams = new URLSearchParams(window.location.search);
  const [courseId, setCourseId] = useState(searchParams.get("courseId") || "");
  const [quotaId, setQuotaId] = useState(searchParams.get("quotaId") || "");
  const [lessonType, setLessonType] = useState(
    searchParams.get("type") || "video"
  );
  const [quotas, setQuotas] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [displayMode, setDisplayMode] = useState("grid");

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [selectedLessonType, setSelectedLessonType] = useState(null);
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [showOptionsMenu, setShowOptionsMenu] = useState(null);

  // Update URL when selections change
  const updateUrlParams = (params) => {
    const searchParams = new URLSearchParams(window.location.search);
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        searchParams.set(key, value);
      } else {
        searchParams.delete(key);
      }
    });
    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    window.history.pushState({}, "", newUrl);
  };

  // Wrapped setState functions to update URL
  const handleSetCourseId = (id) => {
    setCourseId(id);
    updateUrlParams({ courseId: id });
    // Clear quotaId when course changes
    setQuotaId("");
    updateUrlParams({ quotaId: "" });
  };

  const handleSetQuotaId = (id) => {
    setQuotaId(id);
    updateUrlParams({ quotaId: id });
  };

  const handleSetLessonType = (type) => {
    setLessonType(type);
    updateUrlParams({ type: type });
  };

  const fetchLessons = async () => {
    if (!quotaId) return;
    const token = localStorage.getItem("token");
    console.log(token);
    setIsLoading(true);
    setError(null);
    try {
      const endpoint =
        lessonType === "video"
          ? `/lessons/videos/quota/${quotaId}`
          : `/lessons/pdfs/quota/${quotaId}`;

      const response = await axios.get(`${apiBase}${endpoint}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      setLessons(response.data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching lessons:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLessons();
  }, [quotaId, lessonType, refreshFlag]);

  const handleLessonCreated = (newLesson) => {
    setLessons((prev) => [...prev, newLesson]);
    setRefreshFlag(!refreshFlag);
  };

  const handleEditClick = (id, type) => {
    setSelectedLessonId(id);
    setSelectedLessonType(type);
    setShowEditModal(true);
    setShowOptionsMenu(null);
  };

  const handleDeleteClick = (id) => {
    // Add delete functionality here
    setShowOptionsMenu(null);
  };

  const handlePreviewClick = (lesson) => {
    setSelectedLesson(lesson);
    // Add preview functionality
    setShowOptionsMenu(null);
  };

  const toggleOptionsMenu = (id) => {
    setShowOptionsMenu(showOptionsMenu === id ? null : id);
  };

  const filteredLessons = lessons.filter(
    (lesson) =>
      lesson.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lesson.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedLessons = [...filteredLessons].sort((a, b) => {
    if (sortBy === "newest") {
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    } else if (sortBy === "oldest") {
      return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
    } else if (sortBy === "alphabetical") {
      return a.title?.localeCompare(b.title || "");
    }
    return 0;
  });

  // Close options menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setShowOptionsMenu(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <div className="bg-white dark:bg-gray-900 min-h-screen p-1 md:p-6">
      <div className="container mx-auto">
        <div className="mb-8">
          <div className="flex flex-col md:flex-row items-start gap-6  md:items-center justify-between mb-6 md:mb-8 mt-6 md:mt-0">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center">
                <BookOpen className="mr-3" size={28} />
                Lesson Management
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Configure and organize course content
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              disabled={!quotaId}
              className="flex items-center space-x-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors shadow-sm font-medium"
            >
              <PlusCircle size={18} />
              <span>New {lessonType === "video" ? "Video" : "PDF"}</span>
            </button>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-4 sm:items-center">
            <nav className="flex gap-1">
              <button
                onClick={() => handleSetLessonType("video")}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  lessonType === "video"
                    ? "bg-blue-600 text-white shadow-sm"
                    : "bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-750"
                }`}
              >
                <div className="flex items-center">
                  <Video size={18} className="mr-2" />
                  <span>Videos</span>
                </div>
              </button>
              <button
                onClick={() => handleSetLessonType("pdf")}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  lessonType === "pdf"
                    ? "bg-blue-600 text-white shadow-sm"
                    : "bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-750"
                }`}
              >
                <div className="flex items-center">
                  <FileText size={18} className="mr-2" />
                  <span>PDFs</span>
                </div>
              </button>
            </nav>

            <div className="flex-1 flex sm:justify-end space-x-3">
              <div className="relative w-full sm:w-64 md:w-72">
                <input
                  type="text"
                  placeholder="Search lessons..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-600 dark:focus:border-blue-600"
                />
                <Search
                  size={18}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500"
                />
              </div>

              <div className="relative">
                <button
                  className="flex items-center space-x-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-750"
                  onClick={() => {}}
                >
                  <Filter size={18} />
                  <span className="hidden sm:inline">Sort</span>
                  <ChevronDown size={16} />
                </button>
                <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10 hidden">
                  <div className="py-1">
                    <button className="w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-750">
                      Newest First
                    </button>
                    <button className="w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-750">
                      Oldest First
                    </button>
                    <button className="w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-750">
                      Alphabetical
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar with filters and controls */}
          <div className="lg:col-span-3 space-y-6">
            {/* Course Selection */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-5 border-b border-gray-200 dark:border-gray-700">
                <h2 className="font-semibold text-gray-800 dark:text-gray-200 text-lg">
                  Course Selection
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Select a course to manage
                </p>
              </div>
              <div className="p-5">
                <CourseList
                  courseId={courseId}
                  setCourseId={handleSetCourseId}
                />
              </div>
            </div>

            {/* Quota Selection */}
            <div
              className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-200 ${
                !courseId ? "opacity-50 pointer-events-none" : ""
              }`}
            >
              <div className="p-5 border-b border-gray-200 dark:border-gray-700">
                <h2 className="font-semibold text-gray-800 dark:text-gray-200 text-lg">
                  Quota Selection
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Select a quota module
                </p>
              </div>
              <div className="p-5">
                <QuotaList
                  coursesId={courseId}
                  quotaId={quotaId}
                  setQuotaId={handleSetQuotaId}
                  setQuotas={setQuotas}
                />
              </div>
            </div>
          </div>

          {/* Main content area */}
          <div className="lg:col-span-9">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-6">
                {isLoading ? (
                  <LessonSkeletons />
                ) : error ? (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-6 rounded-lg text-red-700 dark:text-red-400 flex items-center justify-center">
                    <span className="text-lg">
                      Error loading lessons: {error}
                    </span>
                  </div>
                ) : !quotaId ? (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-12 rounded-lg text-blue-700 dark:text-blue-400 flex flex-col items-center justify-center">
                    <div className="bg-blue-100 dark:bg-blue-800/50 p-4 rounded-full mb-4">
                      <BookOpen
                        size={36}
                        className="text-blue-600 dark:text-blue-400"
                      />
                    </div>
                    <h3 className="text-xl font-medium mb-2">
                      Get Started with Lesson Management
                    </h3>
                    <p className="text-center text-base max-w-lg mb-6">
                      Start by selecting a course and quota from the panel on
                      the left to view and manage lessons
                    </p>
                    <div className="inline-flex items-center justify-center px-4 py-2 border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <ChevronDown size={20} className="mr-2 animate-bounce" />
                      <span>Select a course to continue</span>
                    </div>
                  </div>
                ) : filteredLessons.length === 0 ? (
                  <div className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 p-16 rounded-lg flex flex-col items-center justify-center text-center">
                    <div className="mb-6 p-4 bg-gray-100 dark:bg-gray-700 rounded-full">
                      {lessonType === "video" ? (
                        <Video
                          size={36}
                          className="text-gray-500 dark:text-gray-400"
                        />
                      ) : (
                        <FileText
                          size={36}
                          className="text-gray-500 dark:text-gray-400"
                        />
                      )}
                    </div>
                    <h3 className="text-xl text-gray-700 dark:text-gray-300 font-medium mb-2">
                      No {lessonType === "video" ? "videos" : "PDFs"} found
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 text-base max-w-md mb-6">
                      {searchQuery
                        ? `No results matching "${searchQuery}". Try adjusting your search.`
                        : `Get started by adding your first ${
                            lessonType === "video" ? "video" : "PDF"
                          } lesson to this quota.`}
                    </p>
                    {!searchQuery && (
                      <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center space-x-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-medium"
                      >
                        <PlusCircle size={18} />
                        <span>
                          Create {lessonType === "video" ? "Video" : "PDF"}{" "}
                          Lesson
                        </span>
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="mb-6 flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">
                        {filteredLessons.length}{" "}
                        {lessonType === "video" ? "Video" : "PDF"} Lesson
                        {filteredLessons.length !== 1 && "s"}
                      </h3>

                      <div className="flex space-x-2">
                        <button
                          onClick={() => setDisplayMode("grid")}
                          className={`p-2 rounded-md ${
                            displayMode === "grid"
                              ? "bg-gray-200 dark:bg-gray-700"
                              : "hover:bg-gray-100 dark:hover:bg-gray-750"
                          }`}
                          aria-label="Grid view"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-gray-700 dark:text-gray-300"
                          >
                            <rect x="3" y="3" width="7" height="7" />
                            <rect x="14" y="3" width="7" height="7" />
                            <rect x="3" y="14" width="7" height="7" />
                            <rect x="14" y="14" width="7" height="7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setDisplayMode("list")}
                          className={`p-2 rounded-md ${
                            displayMode === "list"
                              ? "bg-gray-200 dark:bg-gray-700"
                              : "hover:bg-gray-100 dark:hover:bg-gray-750"
                          }`}
                          aria-label="List view"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-gray-700 dark:text-gray-300"
                          >
                            <line x1="8" y1="6" x2="21" y2="6" />
                            <line x1="8" y1="12" x2="21" y2="12" />
                            <line x1="8" y1="18" x2="21" y2="18" />
                            <line x1="3" y1="6" x2="3.01" y2="6" />
                            <line x1="3" y1="12" x2="3.01" y2="12" />
                            <line x1="3" y1="18" x2="3.01" y2="18" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {displayMode === "grid" ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {sortedLessons.map((lesson) => (
                          <div
                            key={lesson._id}
                            className="group relative flex flex-col border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden hover:shadow-md transition-all bg-white dark:bg-gray-800"
                          >
                            <div className="bg-blue-50 dark:bg-blue-900/20 h-32 flex items-center justify-center">
                              {lessonType === "video" ? (
                                <Video
                                  size={40}
                                  className="text-blue-500 dark:text-blue-400"
                                />
                              ) : (
                                <FileText
                                  size={40}
                                  className="text-blue-500 dark:text-blue-400"
                                />
                              )}
                            </div>
                            <div className="p-5 flex-1">
                              <h4
                                className="font-medium text-gray-900 dark:text-gray-100 text-lg mb-2"
                                title={lesson.title}
                              >
                                {lesson.title}
                              </h4>
                              {lesson.description && (
                                <p
                                  className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2"
                                  title={lesson.description}
                                >
                                  {lesson.description}
                                </p>
                              )}
                            </div>
                            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750 flex justify-between items-center">
                              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                {lesson.date
                                  ? new Date(lesson.date).toLocaleDateString()
                                  : "No date"}
                              </span>
                              <div className="relative">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleOptionsMenu(lesson.lesson_id);
                                  }}
                                  className="p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                >
                                  <MoreHorizontal
                                    size={18}
                                    className="text-gray-600 dark:text-gray-400"
                                  />
                                </button>

                                {showOptionsMenu === lesson.lesson_id && (
                                  <div
                                    className="absolute right-0 bottom-8 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="py-1">
                                      <button
                                        onClick={() =>
                                          handlePreviewClick(lesson)
                                        }
                                        className="w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-750 flex items-center"
                                      >
                                        <Eye size={16} className="mr-2" />
                                        <span>Preview</span>
                                      </button>
                                      <button
                                        onClick={() =>
                                          handleEditClick(
                                            lesson.lesson_id,
                                            lesson.type
                                          )
                                        }
                                        className="w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-750 flex items-center"
                                      >
                                        <Edit size={16} className="mr-2" />
                                        <span>Edit</span>
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-y-scroll">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-750">
                            <tr>
                              <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                              >
                                Title
                              </th>
                              <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                              >
                                Description
                              </th>
                              <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                              >
                                Created
                              </th>
                              <th
                                scope="col"
                                className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                              >
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {sortedLessons.map((lesson) => (
                              <tr
                                key={lesson.lesson_id}
                                className="hover:bg-gray-50 dark:hover:bg-gray-750"
                              >
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                      {lessonType === "video" ? (
                                        <Video
                                          size={20}
                                          className="text-blue-500 dark:text-blue-400"
                                        />
                                      ) : (
                                        <FileText
                                          size={20}
                                          className="text-blue-500 dark:text-blue-400"
                                        />
                                      )}
                                    </div>
                                    <div className="ml-4">
                                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        {lesson.title}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
                                    {lesson.description || "—"}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-500 dark:text-gray-400">
                                    {lesson.createdAt
                                      ? new Date(
                                          lesson.createdAt
                                        ).toLocaleDateString()
                                      : "—"}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <div className="flex items-center justify-end space-x-2">
                                    <button
                                      onClick={() => handlePreviewClick(lesson)}
                                      className="p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
                                      title="Preview"
                                    >
                                      <Eye
                                        size={18}
                                        className='text-blue-500 dark:text-blue-400"'
                                      />
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleEditClick(
                                          lesson.lesson_id,
                                          lesson.type
                                        )
                                      }
                                      className="p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
                                      title="Edit"
                                    >
                                      <Edit size={18} />
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleDeleteClick(lesson.lesson_id)
                                      }
                                      className="hidden p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 text-red-500 dark:text-red-400"
                                      title="Delete"
                                    >
                                      <Trash2 size={18} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Lesson Modal */}
      <CreateLessonModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        lessonType={lessonType}
        quotaId={quotaId}
        courseId={courseId}
        onLessonCreated={handleLessonCreated}
        setRefreshFlag={setRefreshFlag}
      />

      {/* Edit Lesson Modal */}
      <LessonUpdateModal
        lessonId={selectedLessonId}
        lessonType={selectedLessonType}
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setRefreshFlag(!refreshFlag);
        }}
      />
    </div>
  );
};

export default LessonManager;
